import React from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import AvailabilityList from './components/AvailabilityList';
import MarkdownContent from './components/MarkdownContent';
import SimilarBusinesses from './components/SimilarBusinesses';
import TrademarkSearch from './components/TrademarkSearch';
import * as Styled from './ReportSection.style';

const BUBBLE_TITLE = 'Why This Works For You';

/**
 * Component registry for report section content types.
 * Each entry maps a component type string to a render function.
 * The render function receives the content and optional context, returning a React element.
 *
 * To add a new component type:
 * 1. Import the component
 * 2. Add an entry to this registry with the component type as key
 * 3. The PropTypes will automatically include the new type
 */
const COMPONENT_REGISTRY = {
  markdown: (content, { title }) => (
    <MarkdownContent content={content} viewType={title === BUBBLE_TITLE ? 'report' : 'default'} />
  ),
  'availability-list': (content) => <AvailabilityList items={content} />,
  'trademark-search': (content) => <TrademarkSearch data={content} />,
  'similar-businesses': (content) => <SimilarBusinesses items={content} />
};

// Extract valid component types from registry for PropTypes validation
const VALID_COMPONENT_TYPES = Object.keys(COMPONENT_REGISTRY);

/**
 * Individual report section component with icon, title, and content
 * @param {object} props - Component props
 * @param {React.ReactElement} props.icon - Icon component for the section
 * @param {string} props.title - Section title
 * @param {string|object|Array} props.content - Section content (string, JSON object, or array)
 * @param {string} [props.component='markdown'] - Component type from COMPONENT_REGISTRY
 */
function ReportSection({ icon, title, content, component = 'markdown' }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Render content based on component type using registry
  const renderContent = () => {
    const renderer = COMPONENT_REGISTRY[component] || COMPONENT_REGISTRY.markdown;
    return renderer(content, { title });
  };

  return (
    <Styled.ReportSectionRoot>
      {!isMobile && icon && <Styled.SectionIcon>{icon}</Styled.SectionIcon>}
      <Styled.SectionHeader isbubble={title === BUBBLE_TITLE ? 'true' : undefined}>
        <Styled.SectionTitleSection>
          {isMobile && icon && <Styled.SectionIcon>{icon}</Styled.SectionIcon>}
          <Styled.SectionTitle mb={1} size="md">
            {title}
          </Styled.SectionTitle>
        </Styled.SectionTitleSection>

        <Styled.SectionContent size="md">{renderContent()}</Styled.SectionContent>
      </Styled.SectionHeader>
    </Styled.ReportSectionRoot>
  );
}

ReportSection.propTypes = {
  icon: PropTypes.element,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]).isRequired,
  component: PropTypes.oneOf(VALID_COMPONENT_TYPES)
};

export default ReportSection;
